import { useEffect, useRef, useState } from 'react';
import { Container } from '@mui/material';
import { motion } from 'framer-motion';
import style from './NumbersPage.module.css';
import { Sparkline, LineChart, DayGrid } from './charts';

/* ------------- count-up ------------- */
function useCountUp(to: number, duration = 1600): number {
    const [val, setVal] = useState(0);
    const ran = useRef(false);
    useEffect(() => {
        if (ran.current) return;
        ran.current = true;
        const start = performance.now();
        let raf = 0;
        const loop = (now: number) => {
            const t = Math.min(1, (now - start) / duration);
            const ease = 1 - Math.pow(1 - t, 3);
            setVal(to * ease);
            if (t < 1) raf = requestAnimationFrame(loop);
        };
        raf = requestAnimationFrame(loop);
        return () => cancelAnimationFrame(raf);
    }, [to, duration]);
    return val;
}

const nbFmt = new Intl.NumberFormat('nb-NO').format;

function CountUp({ to, format = (n: number) => nbFmt(Math.round(n)) }: { to: number; format?: (n: number) => string }) {
    const v = useCountUp(to);
    return <>{format(v)}</>;
}

/* ------------- mock data ------------- */
type Hue = 'red' | 'gold' | 'soft' | 'ink';

const todayProduction: { label: string; value: string; valueNum: number; format?: (n: number) => string; unit: string; spark: number[]; delta: string; deltaPos: boolean; hue: Hue }[] = [
    { label: 'Esker pakket',     value: '41 250', valueNum: 41250,                                   unit: 'i dag',   spark: [38, 39, 40, 36, 41, 42, 41, 40, 43, 41, 42, 41], delta: '+3,1 %', deltaPos: true,  hue: 'red'  },
    { label: 'Sjokolade smeltet', value: '8 230', valueNum: 8230,                                    unit: 'kg',     spark: [7900, 8050, 8200, 8100, 8400, 8300, 8230],         delta: '+1,4 %', deltaPos: true,  hue: 'gold' },
    { label: 'Kakao brukt',       value: '2 970', valueNum: 2970,                                    unit: 'kg',     spark: [2800, 2900, 2950, 2900, 3000, 2980, 2970],          delta: '+0,8 %', deltaPos: true,  hue: 'soft' },
    { label: 'Energiforbruk',     value: '1 240', valueNum: 1240,                                    unit: 'kWh',    spark: [1380, 1340, 1320, 1290, 1270, 1250, 1240],          delta: '−4,8 %', deltaPos: false, hue: 'ink'  },
];

type LineState = 'kjorer' | 'omstilling' | 'stoppet';
const lines: { name: string; product: string; takt: number; status: LineState; note: string }[] = [
    { name: 'Linje 1', product: 'Stupedama',             takt: 87, status: 'kjorer',    note: '' },
    { name: 'Linje 2', product: 'Rød Fisk — omstilling', takt: 0,  status: 'omstilling', note: 'Planlagt 13:30' },
    { name: 'Linje 3', product: 'Gomp',                  takt: 92, status: 'kjorer',    note: '' },
    { name: 'Pakkeri', product: 'Super Mix · 240 g',     takt: 78, status: 'kjorer',    note: '' },
];

const cocoa = {
    values: [4150, 5400, 7100, 8900, 10800, 11650, 10100, 9400, 9100, 9300, 9180, 9240],
    labels: ['mai', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'des', 'jan', 'feb', 'mar', 'apr'],
    annotations: [
        { index: 4, text: 'Toppen' },
        { index: 11, text: 'I dag' },
    ],
};

const qualityDays: ('ok' | 'warn' | 'fail' | 'future')[] = [
    'ok','warn','ok','ok','ok','ok','ok',
    'warn','ok','ok','ok','ok','ok','ok',
    'ok','ok','ok','ok','ok','ok','ok',
    'ok','ok','ok','ok','ok','ok','ok',
];

/* ------------- component ------------- */
export default function NumbersPage() {
    return (
        <Container maxWidth={false} className={style.page}>
            <motion.header
                className={style.pageHead}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.45 }}
            >
                <h1 className={style.pageTitle}>
                    Brynild <span className={style.pageTitleItalic}>i tall</span>
                </h1>
                <span className={style.headOrnament} aria-hidden="true">✦ ✦ ✦</span>
                <div className={style.headTime}>
                    <span className={style.liveDot} aria-hidden="true" />
                    <span>Sanntid · Oppdatert 10:42</span>
                </div>
            </motion.header>

            {/* ROW 1 — RED HERO + STATS */}
            <section className={style.rowOne}>
                <motion.article
                    className={style.heroPanel}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
                >
                    <div className={style.heroGrain} aria-hidden="true" />
                    <div className={style.heroPanelInner}>
                        <div className={style.heroLabels}>
                            <span className={style.heroKicker}>Hittil i mai</span>
                            <span className={style.heroBadge}>+4,2 %</span>
                        </div>
                        <div className={style.heroNumber}>
                            <CountUp to={1280000} />
                        </div>
                        <div className={style.heroUnit}>esker pakket</div>
                        <div className={style.heroDek}>
                            Det høyeste mai-tallet siden 2019 — drevet av Stupedama og Rød Fisk.
                        </div>
                    </div>
                    <div className={style.heroOrnament} aria-hidden="true">
                        <span>BRYNILD</span><span>·</span><span>SIDEN&nbsp;1895</span>
                    </div>
                </motion.article>

                <article className={style.statsPanel}>
                    <header className={style.smallHead}>
                        <span className={style.smallKicker}>I dag</span>
                        <h2 className={style.smallTitle}>Linja siste timer</h2>
                    </header>
                    <div className={style.statGrid}>
                        {todayProduction.map((s, i) => (
                            <motion.div
                                key={s.label}
                                className={`${style.stat} ${style[`hue_${s.hue}`]}`}
                                initial={{ opacity: 0, y: 8 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.4, delay: 0.1 + i * 0.06 }}
                            >
                                <div className={style.statLabel}>{s.label}</div>
                                <div className={style.statRow}>
                                    <div className={style.statNumber}>
                                        <CountUp to={s.valueNum} />
                                    </div>
                                    <span className={style.statUnit}>{s.unit}</span>
                                </div>
                                <div className={style.statBottom}>
                                    <span className={`${style.statDelta} ${s.deltaPos ? style.deltaPos : style.deltaNeg}`}>
                                        {s.delta}
                                    </span>
                                    <Sparkline values={s.spark} accent={s.deltaPos} width={64} height={20} />
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </article>
            </section>

            {/* ROW 2 — 3 COL: Akkurat nå · Kakao · Kvalitet */}
            <section className={style.rowTwo}>
                <article className={`${style.block} ${style.blockWarm}`}>
                    <header className={style.blockHead}>
                        <div className={style.blockKicker}>Sanntid</div>
                        <h2 className={style.blockTitle}>Akkurat nå</h2>
                        <span className={style.liveDotSmall} aria-hidden="true" />
                    </header>
                    <div className={style.linesList}>
                        {lines.map((l, i) => (
                            <motion.div
                                key={l.name}
                                className={`${style.lineItem} ${style[`status_${l.status}`]}`}
                                initial={{ opacity: 0, x: -6 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.4, delay: 0.15 + i * 0.05 }}
                            >
                                <div className={style.lineMeta}>
                                    <span className={style.lineName}>{l.name}</span>
                                    <span className={style.linePill}>
                                        {l.status === 'kjorer' && 'Kjører'}
                                        {l.status === 'omstilling' && 'Omstilling'}
                                        {l.status === 'stoppet' && 'Stoppet'}
                                    </span>
                                </div>
                                <div className={style.lineProduct}>{l.product}</div>
                                <div className={style.lineFooter}>
                                    <div className={style.lineTrack}>
                                        <div className={style.lineFill} style={{ width: `${Math.max(2, l.takt)}%` }} />
                                    </div>
                                    <span className={style.lineTakt}>{l.takt}<span className={style.percent}>%</span></span>
                                </div>
                                {l.note && <div className={style.lineNote}>{l.note}</div>}
                            </motion.div>
                        ))}
                    </div>
                </article>

                <article className={`${style.block} ${style.blockCream}`}>
                    <header className={style.blockHead}>
                        <div className={style.blockKicker}>Råvarer · 12 mnd</div>
                        <h2 className={style.blockTitle}>Kakaoprisen</h2>
                        <span className={style.blockMeta}>USD/tonn</span>
                    </header>
                    <div className={style.cocoaTop}>
                        <div className={style.cocoaNumber}>
                            <CountUp to={9240} format={(n) => nbFmt(Math.round(n))} />
                            <span className={style.cocoaUnit}>&thinsp;USD/t</span>
                        </div>
                        <span className={style.cocoaDeltaPos}>+138 % / 5&thinsp;år</span>
                    </div>
                    <div className={style.chartWrap}>
                        <LineChart values={cocoa.values} labels={cocoa.labels} annotations={cocoa.annotations} height={88} />
                    </div>
                </article>

                <article className={`${style.block} ${style.blockGold}`}>
                    <header className={style.blockHead}>
                        <div className={style.blockKicker}>Kvalitet · 28 dager</div>
                        <h2 className={style.blockTitle}>Linje 1 uten avvik</h2>
                    </header>
                    <div className={style.qualityTop}>
                        <div className={style.qualityNumber}>
                            <CountUp to={14} />
                        </div>
                        <div className={style.qualityUnit}>
                            <span className={style.qualityUnitMain}>dager</span>
                            <span className={style.qualityUnitSub}>uten registrert avvik</span>
                        </div>
                    </div>
                    <div className={style.qualityGrid}>
                        <DayGrid days={qualityDays} dayLabels={['Ma','Ti','On','To','Fr','Lø','Sø']} />
                    </div>
                    <div className={style.qualityFoot}>
                        Bedriftsrekord <strong>72 d</strong> · Pakkeri på <strong>198 d</strong>
                    </div>
                </article>
            </section>

        </Container>
    );
}
